import {
  type ApolloClient,
  ApolloError,
  type ApolloQueryResult,
  NetworkStatus,
} from "@apollo/client";
import { createApolloClient } from "@package/ui-apollo";
import { useAuthState } from "@package/ui-authentication";
import { useConfigState } from "@package/ui-config";
import {
  GetEventsDocument,
  type GetEventsQuery,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { useOrganizationState } from "@package/ui-organization";
import { useEffect, useState } from "react";

type UseGetEventsResponse = ApolloQueryResult<GetEventsQuery> & {
  refetch: (
    refetchVariables?: GetEventsQueryVariables,
  ) => Promise<ApolloQueryResult<GetEventsQuery>>;
};

const defaultQueryResult: ApolloQueryResult<GetEventsQuery> = {
  data: {
    items: [],
    total: {
      __typename: "ListMetadata",
      count: 0,
    },
  },
  loading: false,
  networkStatus: NetworkStatus.ready,
};

export function useGetEvents(
  variables: GetEventsQueryVariables,
  skip = false,
  apolloClient?: ApolloClient<object>,
) {
  const [client, setClient] = useState<ApolloClient<object>>();
  const [response, setResponse] = useState<UseGetEventsResponse>({
    ...defaultQueryResult,
    refetch: async () => defaultQueryResult,
  });

  const { apollo, baseUrlApiAnalytics, environment } = useConfigState();
  const { token, tokenDecoded } = useAuthState();
  const { selectedOrganization } = useOrganizationState();

  useEffect(
    function effectOnApolloClient() {
      const createClient = async () => {
        if (token && tokenDecoded) {
          setClient(
            await createApolloClient({
              ...apollo,
              environment,
              organization: selectedOrganization?.orgId,
              token,
              uri: `${baseUrlApiAnalytics}/api/graphql`,
            }),
          );
        }
      };

      if (apolloClient) {
        setClient(apolloClient);
      } else {
        createClient();
      }
    },
    [
      apollo,
      apolloClient,
      baseUrlApiAnalytics,
      environment,
      selectedOrganization?.orgId,
      token,
      tokenDecoded,
    ],
  );

  useEffect(
    function effectOnFetchEvents() {
      const fetchEvents = async (
        variablesOverride?: GetEventsQueryVariables,
      ) => {
        try {
          const r = await client?.query<
            GetEventsQuery,
            GetEventsQueryVariables
          >({
            query: GetEventsDocument,
            variables: variablesOverride ?? variables,
          });
          if (r) {
            setResponse({
              ...r,
              refetch: async (refetchVariables?: GetEventsQueryVariables) =>
                (await fetchEvents(refetchVariables)) ?? defaultQueryResult,
            });
          }

          return r;
        } catch (error: unknown) {
          console.error("Error fetching events:", error);
          setResponse((prev) => ({
            ...prev,
            error: new ApolloError({ errorMessage: "Error fetching events" }),
            loading: false,
          }));
        }
      };

      if (!skip) {
        fetchEvents();
      }
    },
    [client, skip, variables],
  );

  return response;
}
